import moment from 'common/moment';
import App from 'common/backbone-app';
import Wahanda from 'common/wahanda';
type Row = Record<string, unknown>;

export type QuoteReportEntryType = 'QUOTE' | 'VOID';

type QuoteVoidFields = Pick<QuoteSummaryData, 'type'> &
  Partial<Pick<QuoteSummaryData, 'created' | 'referenceOperationTime' | 'voidInfo'>>;

interface QuoteLineItem {
  name: string;
  amount: number;
  taxRate: number;
  taxAmount: number;
  type?: string;
}

interface QuoteEntry {
  quoteId: number;
  created: string;
  venueLocalDateTime: string;
  employeeId?: number;
  employeeName?: string;
  grossAmount: number;
  currencyCode: string;
  venueCustomerName?: string;
  venueQuoteNumber: number;
  type: QuoteReportEntryType;
  transactionId: string;
  referenceOperationTime?: string;
  lineItems?: QuoteLineItem[];
  venueCustomerId?: number;
}

type VoidQuoteInfo = {
  voidQuoteId?: number;
  created: string;
  venueLocalDateTime?: string;
  employeeName?: string;
};

export interface QuoteSummaryData {
  venueId: number;
  venueQuoteNumber: number;
  transactionId: string;
  created: string;
  venueLocalDateTime?: string;
  type?: QuoteReportEntryType;
  referenceOperationTime?: string;
  employeeId?: number;
  employeeName: string;
  walkIn: boolean;
  venueCustomerName?: string;
  venueCustomerId?: number;
  grossAmount: number;
  serviceLineItems: Array<{
    primaryServiceLineItemConstituentId: number;
    name: string;
    originalAmount: number;
    serviceLineItemConstituents?: Array<{ id: number; name: string }>;
  }>;
  voidInfo?: VoidQuoteInfo;
  voidQuote?: VoidQuoteInfo;
}

export interface QuoteDailyReport {
  date: string;
  totalGrossAmount: number;
  currencyCode: string;
  quotes: QuoteEntry[];
}

const getVoidInfoFromQuoteResponse = (
  response?: Pick<QuoteSummaryData, 'voidInfo' | 'voidQuote'> | null,
): VoidQuoteInfo | undefined => response?.voidInfo ?? response?.voidQuote;

export const buildQuoteSummaryDataFromTransactionRow = (transaction: Row): QuoteSummaryData => {
  const lineItems = (transaction.lineItems as QuoteLineItem[] | undefined) ?? [];
  const venueCustomerName = transaction.venueCustomerName as string | undefined;

  return {
    venueId: App.getVenueId(),
    venueQuoteNumber: transaction.venueQuoteNumber as number,
    transactionId: transaction.transactionId as string,
    created: transaction.created as string,
    venueLocalDateTime: transaction.venueLocalDateTime as string,
    type: transaction.type as QuoteReportEntryType,
    referenceOperationTime: transaction.referenceOperationTime as string | undefined,
    employeeId: transaction.employeeId as number | undefined,
    employeeName: (transaction.employeeName as string | undefined) ?? '',
    walkIn: !venueCustomerName,
    venueCustomerName,
    venueCustomerId: transaction.venueCustomerId as number | undefined,
    grossAmount: (transaction.grossAmount as number | undefined) ?? (transaction.amount as number),
    serviceLineItems: lineItems.map((item, index) => ({
      primaryServiceLineItemConstituentId: index,
      name: item.name,
      originalAmount: item.amount,
    })),
  };
};

export const normalizeQuoteVoidFields = <T extends QuoteVoidFields>(data: T): T => {
  if (data.referenceOperationTime != null || !data.voidInfo) {
    return data;
  }

  return {
    ...data,
    referenceOperationTime: data.voidInfo.created,
  };
};

export const isUsableVoidPatchResponse = (response?: QuoteSummaryData | null): boolean => {
  if (!response) {
    return false;
  }

  if (getVoidInfoFromQuoteResponse(response)?.created) {
    return true;
  }

  return isVoidQuote(normalizeQuoteVoidFields(response));
};

export const buildVoidedQuoteDataFromVoidSuccess = (
  data: QuoteSummaryData,
  response?: QuoteSummaryData | null,
): QuoteSummaryData => {
  const voidInfo = getVoidInfoFromQuoteResponse(response);
  const voidCreated =
    voidInfo?.created ??
    (response?.created && moment(response.created).isAfter(data.created)
      ? response.created
      : moment().toISOString());

  return {
    ...data,
    ...(response ?? {}),
    type: 'VOID',
    created: voidCreated,
    venueLocalDateTime: voidInfo?.venueLocalDateTime,
    referenceOperationTime: data.created,
    employeeName: voidInfo?.employeeName ?? response?.employeeName ?? data.employeeName,
  };
};

export const resolveVoidedQuoteDataAfterSuccess = (
  data: QuoteSummaryData,
  response?: QuoteSummaryData | null,
): QuoteSummaryData => {
  const voidedData = normalizeQuoteVoidFields(applyVoidedQuoteResponse(data, response));

  if (isVoidQuote(voidedData)) {
    return voidedData;
  }

  return buildVoidedQuoteDataFromVoidSuccess(data, response);
};

export const isVoidQuote = (
  data: Pick<QuoteSummaryData, 'type' | 'referenceOperationTime' | 'voidInfo'>,
): boolean => data.type === 'VOID' || data.referenceOperationTime != null || data.voidInfo != null;

export const isVoidQuoteEntry = (data: QuoteVoidFields): boolean => {
  if (data.type === 'VOID') {
    return true;
  }
  if (data.type === 'QUOTE') {
    return false;
  }

  const normalized = normalizeQuoteVoidFields(data);
  if (normalized.created == null) {
    return false;
  }

  return (
    normalized.referenceOperationTime != null &&
    moment(normalized.created).isAfter(normalized.referenceOperationTime)
  );
};

export const applyVoidedQuoteResponse = (
  data: QuoteSummaryData,
  response?: QuoteSummaryData | null,
): QuoteSummaryData => {
  const voidInfo = getVoidInfoFromQuoteResponse(response);
  if (voidInfo?.created) {
    return buildVoidedQuoteDataFromVoidSuccess(data, response);
  }

  if (!response) {
    return data;
  }

  const normalizedResponse = normalizeQuoteVoidFields(response);
  if (!isVoidQuote(normalizedResponse)) {
    return data;
  }

  if (isVoidQuoteEntry(normalizedResponse)) {
    return {
      ...data,
      ...normalizedResponse,
      type: 'VOID',
      venueLocalDateTime: normalizedResponse.venueLocalDateTime,
    };
  }

  return {
    ...data,
    ...normalizedResponse,
    type: 'VOID',
    created: normalizedResponse.referenceOperationTime!,
    venueLocalDateTime: undefined,
    referenceOperationTime: data.created,
    employeeName: normalizedResponse.employeeName ?? data.employeeName,
  };
};

const compareByCreated = (firstRow: Row, secondRow: Row): number => {
  const firstCreatedAt = firstRow.created ?? '';
  const secondCreatedAt = secondRow.created ?? '';

  if (firstCreatedAt > secondCreatedAt) {
    return -1;
  }
  if (firstCreatedAt < secondCreatedAt) {
    return 1;
  }
  return 0;
};

const toTransactionRow = (entry: QuoteEntry): Row => ({
  quoteId: entry.quoteId,
  created: entry.created,
  venueLocalDateTime: entry.venueLocalDateTime,
  type: entry.type,
  payments: [],
  amount: entry.grossAmount,
  lineItems: entry.lineItems ?? [],
  customerName: entry.venueCustomerName ?? Wahanda.lang.shared.walkin,
  transactionId: entry.transactionId,
  referenceOperationTime: entry.referenceOperationTime,
  venueQuoteNumber: entry.venueQuoteNumber,
  employeeId: entry.employeeId,
  employeeName: entry.employeeName,
  venueCustomerId: entry.venueCustomerId,
  venueCustomerName: entry.venueCustomerName,
  time: moment(entry.created).format('HH:mm:ss'),
});

export const mergeQuotesIntoDailyReport = (
  posDailyReport: Row,
  quoteDailyReport: QuoteDailyReport | null,
): Row => {
  const transactions: Row[] = Array.isArray(posDailyReport.transactions)
    ? [...(posDailyReport.transactions as Row[])]
    : [];
  const quoteEntries = quoteDailyReport?.quotes ?? [];
  const mergedTransactions = [...transactions, ...quoteEntries.map(toTransactionRow)].sort(
    compareByCreated,
  );

  return {
    ...posDailyReport,
    transactions: mergedTransactions,
    ...(quoteDailyReport && {
      quotesSummary: {
        totalGrossAmount: quoteDailyReport.totalGrossAmount ?? 0,
        currencyCode: quoteDailyReport.currencyCode,
      },
    }),
  };
};
